import { formatSecondToMMss } from '@doltech/utils/lib/date';
import cl from 'classnames';
import { isNil } from 'lodash';
import { observer } from 'mobx-react-lite';
import * as React from 'react';
import ReactPlayer, { FileConfig } from 'react-player/file';
import ReactSlider from 'react-slider';
import { useDebounce, useRafState } from 'react-use';
import styled from 'styled-components';

import { figmaColors } from '../../colors';
import { colorsV2 } from '../../colors-v2';
import { Typography } from '../../DesignSystemV2/Typography';
import { useUncontrolledAudioPlayerStore } from './uncontrolled-audio-player.store';

/**
 * Note for maintainers:
 * - I want this compoinent to be uncontrolled unlike the FlatAudioPlayer which got many redundant feature
 */

const audioFetchConfig: FileConfig = {
  forceAudio: true,
  attributes: {
    controls: true,
    crossOrigin: 'anonymous',
    preload: 'metadata',
  },
};

interface DurationComponentProps {
  progressInSeconds?: number;
  durationInSeconds: number;
}

export const DurationComponent = observer(
  ({ progressInSeconds, durationInSeconds }: DurationComponentProps) => {
    const [localProgressInSeconds, setLocalProgressInSeconds] = useRafState(0);
    useDebounce(
      () => {
        setLocalProgressInSeconds(progressInSeconds);
      },
      150,
      [progressInSeconds]
    );

    return (
      <Typography.Paragraph className="duration-component" variant="medium/14">
        {!isNil(progressInSeconds) && (
          <Typography.Text>{formatSecondToMMss(localProgressInSeconds)}</Typography.Text>
        )}
        <Typography.Text color="black70">
          {!isNil(progressInSeconds) && ' / '}
          {formatSecondToMMss(durationInSeconds)}s
        </Typography.Text>
      </Typography.Paragraph>
    );
  }
);

export const DurationComponentCompact = observer(
  ({ progressInSeconds, durationInSeconds }: DurationComponentProps) => {
    const [localProgressInSeconds, setLocalProgressInSeconds] = useRafState(0);
    useDebounce(
      () => {
        setLocalProgressInSeconds(progressInSeconds);
      },
      150,
      [progressInSeconds]
    );

    return (
      <Typography.Paragraph className="duration-component" variant="medium/14">
        {!isNil(progressInSeconds) && (
          <Typography.Text>{formatSecondToMMss(localProgressInSeconds)}</Typography.Text>
        )}
        <Typography.Text color="black70">
          {!isNil(progressInSeconds) && ' / '}
          {formatSecondToMMss(durationInSeconds)}
        </Typography.Text>
      </Typography.Paragraph>
    );
  }
);

export interface UncontrolledAudioPlayerProps extends React.HTMLAttributes<HTMLDivElement> {
  url: string;
  volume?: number;
  interactive?: boolean;
}

export const UncontrolledAudioPlayer = observer((props: UncontrolledAudioPlayerProps, ref) => {
  const { url, title, onEnded, volume = 1, onPlay, onPause, interactive, ...rest } = props;
  const store = useUncontrolledAudioPlayerStore();
  const audioPlayer = React.useRef<any>(null);

  const renderThumb = React.useCallback((renderProps) => {
    return (
      <div {...renderProps}>
        <div className="thumb-circle" />
      </div>
    );
  }, []);

  return (
    <Container {...rest}>
      <ReactPlayer
        config={audioFetchConfig}
        playing={store.isPlaying}
        playsinline
        className="hidden"
        ref={audioPlayer}
        muted={volume === 0}
        volume={volume}
        url={url}
        onDuration={(duration) => {
          store.setDurationInSeconds(duration);
          store.setIsReady(true);
        }}
        onProgress={(state) => {
          store.setProgressInSeconds(state.playedSeconds);
        }}
        onEnded={() => {
          store.setProgressInSeconds(store.durationInSeconds);
          store.setSlideProgressInSeconds(store.durationInSeconds);
          store.setPlaying(false);
        }}
      />

      <ReactSlider
        className="slider-container"
        thumbClassName={cl('thumb', 'mode-circle')}
        trackClassName="track"
        defaultValue={0}
        value={store.progressInSeconds}
        max={store.durationInSeconds}
        ariaLabel={['Lowest thumb', 'Middle thumb', 'Top thumb']}
        renderThumb={renderThumb}
        pearling
        onChange={(v) => {
          if (store.isPlaying) {
            store.setSeekInPlaying(true);
          }
          store.setPlaying(false);
          store.setSlideProgressInSeconds(v);
        }}
        onAfterChange={(value) => {
          audioPlayer.current.seekTo(value, 'seconds');
          if (store.seekWhenPlaying) {
            store.setPlaying(true);
          }
          store.setSeekInPlaying(false);
          store.setProgressInSeconds(store.sliderProgressInSeconds);
        }}
        disabled={!interactive}
      />

      <DurationComponent
        progressInSeconds={
          store.isPlaying ? store.progressInSeconds : store.sliderProgressInSeconds
        }
        durationInSeconds={store.durationInSeconds}
      />
    </Container>
  );
});

const Container = styled.div`
  display: grid;
  grid-auto-flow: column;
  grid-template-columns: 1fr minmax(0, max-content);
  gap: 16px;
  align-items: center;

  .hidden {
    display: none;
  }

  .slider-container {
    width: 100%;
    height: 18px;
    display: flex;
    justify-content: flex-start;
    align-items: center;
    position: relative;
    .track {
      height: 2px;
      transition: all 0.05s ease;
      &.track-0 {
        background-color: ${colorsV2.primary100};
      }
      &.track-1 {
        background-color: ${colorsV2.gray20};
      }
    }
  }

  .thumb {
    user-select: none;
    font-size: 8px;
    font-weight: 700;
    color: ${colorsV2.white100};
    background: ${colorsV2.primary100};
    padding: 0 4px;
    border-radius: 12px;
    transition: all 0.05s ease;

    &.mode-circle {
      padding: 0;
      border-radius: 0;
      background-color: transparent;
    }
    .thumb-circle {
      background: ${colorsV2.white100};
      border: 2px solid ${colorsV2.primary80};
      width: 14px;
      height: 14px;
      border-radius: 50%;
    }
  }

  .slider {
    transition: all 0.05s ease;
    width: 100%;
    cursor: move;
    height: 100%;
    transform-origin: 0% 50%;
    background-color: ${figmaColors.primary5};
  }
`;
